import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Mail,
  Lock,
  User,
  ArrowRight,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import { adminAPI } from "../services/api";

const AdminRegister = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    full_name: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      setLoading(false);
      return;
    }

    try {
      await adminAPI.register(
        formData.email,
        formData.password,
        formData.full_name,
      );

      setSuccess(true);

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/admin/login");
      }, 2000);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Registration failed. Email may already be registered.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-[var(--background)]">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-12">
          <Link to="/" className="inline-block mb-8">
            <h1 className="font-headline text-2xl font-bold text-[var(--primary)] uppercase tracking-headline">
              Kravion
            </h1>
          </Link>
          <h2 className="font-headline text-3xl font-bold uppercase mb-2">
            Create Admin Account
          </h2>
          <p className="font-body text-[var(--on-surface-variant)]">
            Register to access the admin panel
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-[var(--error-container)] border-l-4 border-[var(--error)] flex items-start gap-3">
            <AlertCircle
              size={20}
              className="text-[var(--error)] flex-shrink-0 mt-0.5"
            />
            <p className="font-body text-sm text-[var(--error)]">{error}</p>
          </div>
        )}

        {/* Success Alert */}
        {success && (
          <div className="mb-6 p-4 bg-[var(--tertiary-container)]/20 border-l-4 border-[var(--tertiary)] flex items-start gap-3">
            <CheckCircle
              size={20}
              className="text-[var(--tertiary)] flex-shrink-0 mt-0.5"
            />
            <p className="font-body text-sm text-[var(--tertiary)]">
              Account created successfully! Redirecting to login...
            </p>
          </div>
        )}

        {/* Register Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-[var(--surface-container)] p-8 space-y-6"
        >
          {/* Full Name */}
          <div>
            <label
              htmlFor="full_name"
              className="block font-body text-xs tracking-code uppercase text-[var(--on-surface-variant)] mb-3"
            >
              Full Name
            </label>
            <div className="relative">
              <User
                size={20}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--on-surface-variant)]"
              />
              <input
                type="text"
                id="full_name"
                name="full_name"
                required
                value={formData.full_name}
                onChange={handleChange}
                className="w-full bg-[var(--surface-container-high)] border border-[var(--outline-variant)]/20 focus:border-[var(--primary)] outline-none pl-12 pr-4 py-3 font-body text-[var(--on-surface)] transition-colors duration-300"
                placeholder="John Doe"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className="block font-body text-xs tracking-code uppercase text-[var(--on-surface-variant)] mb-3"
            >
              Email Address
            </label>
            <div className="relative">
              <Mail
                size={20}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--on-surface-variant)]"
              />
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full bg-[var(--surface-container-high)] border border-[var(--outline-variant)]/20 focus:border-[var(--primary)] outline-none pl-12 pr-4 py-3 font-body text-[var(--on-surface)] transition-colors duration-300"
                placeholder="admin@example.com"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label
              htmlFor="password"
              className="block font-body text-xs tracking-code uppercase text-[var(--on-surface-variant)] mb-3"
            >
              Password
            </label>
            <div className="relative">
              <Lock
                size={20}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--on-surface-variant)]"
              />
              <input
                type="password"
                id="password"
                name="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="w-full bg-[var(--surface-container-high)] border border-[var(--outline-variant)]/20 focus:border-[var(--primary)] outline-none pl-12 pr-4 py-3 font-body text-[var(--on-surface)] transition-colors duration-300"
                placeholder="••••••••"
              />
            </div>
            <p className="mt-2 font-body text-xs text-[var(--on-surface-variant)]">
              Minimum 6 characters
            </p>
          </div>

          {/* Confirm Password */}
          <div>
            <label
              htmlFor="confirmPassword"
              className="block font-body text-xs tracking-code uppercase text-[var(--on-surface-variant)] mb-3"
            >
              Confirm Password
            </label>
            <div className="relative">
              <Lock
                size={20}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--on-surface-variant)]"
              />
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="w-full bg-[var(--surface-container-high)] border border-[var(--outline-variant)]/20 focus:border-[var(--primary)] outline-none pl-12 pr-4 py-3 font-body text-[var(--on-surface)] transition-colors duration-300"
                placeholder="••••••••"
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || success}
            className="magnetic w-full bg-gradient-to-br from-[var(--primary)] to-[var(--primary-container)] text-[var(--on-primary-container)] px-8 py-4 font-headline font-bold text-sm uppercase tracking-code hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2 group"
          >
            {loading
              ? "Creating Account..."
              : success
                ? "Account Created!"
                : "Create Account"}
            {!loading && !success && (
              <ArrowRight
                size={18}
                className="group-hover:translate-x-1 transition-transform"
              />
            )}
          </button>
        </form>

        {/* Login Link */}
        <p className="text-center mt-6 font-body text-sm text-[var(--on-surface-variant)]">
          Already have an account?{" "}
          <Link
            to="/admin/login"
            className="text-[var(--primary)] hover:brightness-110 font-bold"
          >
            Login here
          </Link>
        </p>
      </div>
    </div>
  );
};

export default AdminRegister;
